import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type AddUserFormSchema, addUserFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { AddUserForm } from "@/forms/add-user-form";
import { client } from "@/hono";

/** Props Interface */
interface AddUserModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
}

export function AddUserModal({ isOpen, onClose, onComplete }: AddUserModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm({
		resolver: resolver(addUserFormSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: AddUserFormSchema) {
		try {
			const response = await client.organizations.user.$post({ json });
			// One message per outcome, raised here — the same shape every other create in the
			// role surface uses. Creating a person used to end in a silent close: no toast, and
			// a members table that still showed the old list until a reload.
			await responseHandler(response, false);
			toast.success(t("users.user_added"));
			// Every list of people hangs off a key containing "members" — the overview table and
			// the pickers alike — so one invalidation reaches them all.
			await qc.invalidateQueries({ predicate: invalidate(["members"]) });
			await onComplete?.();
			onClose();
		} catch (error) {
			// The form keeps its input on failure, so a refused create can be corrected and
			// retried without retyping the person.
			toast.error(error instanceof Error ? error.message : t("general.something_went_wrong"));
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("users.add_user")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<AddUserForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
